import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from 'classnames';
import { HeaderUserIcon, EditDarkIcon as EditIcon } from "@netapp/bxp-design-system-react/icons/monochrome";
import { UserSettingsIcon } from "@netapp/bxp-style/react-icons/Action";
import styles from "./../MainHeader.module.scss";
import userMenuStyles from "./UserMenu.module.scss";
import SlidingWidget from "./SlidingWidget"
import { StyledLink, Button, Span, Toggle, Tag, Tooltip, FormFieldInputNew } from "@netapp/bxp-design-system-react";
import buttons from "@netapp/bxp-design-system-react/styles/buttons.module.scss";
import { useTranslation } from "react-i18next";
import { allMenus as i18nNamespace } from "../../../../i18n/namespace";

const StaticDetails = ({
    children,
    label,
    value,
    isEditable=false,
    handleEdit,
}) => {
    return (
        <div className={userMenuStyles["detail"]}>
            <div className={classNames(styles["static-details"])}>
                <div>
                    {children}
                    <div className={classNames(userMenuStyles["details-value"], styles["info"])}>
                        {value}
                    </div>
                    <div className={userMenuStyles["details-label"]}>
                        {label}
                    </div>
                </div>
                {isEditable  && (
                     <button className={classNames(styles['edit-btn'])}
                         onClick={
                                (e) => {
                                    e.stopPropagation();
                                    handleEdit(true);
                                }
                         }>
                     <EditIcon />
                 </button>
                )}
            </div>
        </div>
    );
};


const DarkTheme = () => {
    const dispatch = useDispatch();
    const isActive = useSelector(state => !!state.features.active["Platform.BlueXP/DarkTheme"]);
    const { t } = useTranslation(i18nNamespace);
    return <div className={userMenuStyles["detail"]}>
        <div style={{ marginBottom: 8 }}>
            <Span style={{ marginRight: 8 }}>{t("dark_theme")}</Span>
            <Tag color="chart-9">{t("beta")}
                <Tooltip>
                    {isActive ? <>
                        <div>{t("there_is_a_crack_a_crack_in_everything")}</div>
                        <div>{t("thats_how_the_light_gets_in")}</div>
                    </> : <>
                            <div>{t("you_want_it_darker")}</div>
                            <div>{t("we_kill_the_flame")}</div>
                        </>}
                </Tooltip>
            </Tag>
        </div>
        <Toggle value={isActive} ariaLabel={t("dark_theme")} toggle={() => {
            dispatch({
                type: "FEATURES:TOGGLE-FEATURE",
                payload: {
                    feature: "Platform.BlueXP/DarkTheme"
                }
            })
        }}/>
    </div>
}

const UserNameEditForm = ({ handleNameChange, handleCancel, handleSubmit, userName, error, isLoading }) => {
    const { t } = useTranslation(i18nNamespace);
    return (
        <div className={styles["edit-name-form"]}>
        <form onSubmit={(e) => {e.preventDefault(); handleSubmit();}}>
            <div className={classNames(styles["content"])}>
                <div className={styles["input-restrict"]}>
                    <FormFieldInputNew
                        type="text"
                        name="Name"
                        label={t("name")}
                        value={userName}
                        onChange={event => handleNameChange(event)}
                        errorMessage={error}
                        showError={!!error}
                    />
                </div>
                <div className={styles["button-wrapper"]}>
                    <Button submit type="text" isSubmitting={isLoading}>{t("save")}</Button>
                    <button onClick={(e) => {
                                e.stopPropagation();
                                handleCancel(false)
                            }} 
                        className={classNames(buttons["text-secondary"], styles["button-space"])}>
                                {t("cancel")}
                    </button>
                </div>
            </div>
        </form>
    </div>
    );
};

export default () => {
    const dispatch = useDispatch();
    const { name, email, company, isNetAppInternal, isFederatedUser, getUserNamePending, getUserNameShowError } = useSelector(state => state.auth);
    const isActive = useSelector(state => state.menu.activeWidget);
    const [editName, setEditName] = useState(false);
    const [userName, setUserName] = useState(name);
    const [error, setError] = useState('');
    const { t } = useTranslation(i18nNamespace);

    const isDarkSite = process.env.REACT_APP_IS_DARK === 'true';

    useEffect(() =>{
        if(!isActive || (!getUserNamePending && !getUserNameShowError)) {
            setEditName(false);
            setUserName(name);
        } 
    }, [isActive, getUserNamePending, getUserNameShowError]);

    useEffect(() =>{
        if (getUserNameShowError) {
            setError(t("failed_to_update_the_name"));
        }
    }, [getUserNameShowError]);


    const handleNameChange  = (event) => {
        setUserName(event.target.value);
    }

    const handleShowHideUserName = (isEditUserName) => { 
        setEditName(isEditUserName);
        setError('');
        if (!isEditUserName) {
            setUserName(name);
        }
    }

    const handleNameSubmit = async () => {
        if(userName.length < 1){
            setError(t("name_is_required"));
            return;
        }

        if(userName.length > 255){
            setError(t("name_cannot_be_more_than_255_characters"));
            return;
        }

        if (userName !== name) {
            dispatch({
                type: "USER:UPDATE-USERNAME",
                payload: {
                    userName
                }
            })
        }  
    }

    return <SlidingWidget
        widgetKey="user"
        menuTitle={t("user_settings")}
        MenuIcon={UserSettingsIcon}
        MenuActionButton={() => {
            return <Button onClick={() => dispatch({
                type: "AUTH:LOGOUT"
            })}>{t("logout")}</Button>
        }}
        isActive={isActive}
        triggerClassName={styles['widget-button']}
        triggerActiveClassName={[styles['widget-button-active']]}
        triggerChildren={<HeaderUserIcon size={24} display="block"/>}
    >
            <div className={userMenuStyles["content"]}>
            {
            
                editName ? (
                    <UserNameEditForm
                        handleNameChange={handleNameChange}
                        handleCancel={handleShowHideUserName}
                        handleSubmit={handleNameSubmit}
                        userName={userName}
                        error={error}
                        isLoading={getUserNamePending}
                    />
                ) : (
                    <StaticDetails
                        handleEdit={handleShowHideUserName}
                        isEditable={!isFederatedUser && !isDarkSite}
                        label={t("name")}
                        value={name}
                    />
                )
            }

                <StaticDetails label={t("email")} value={email}/>
                <StaticDetails label={t("company")} value={company}>
                    {isNetAppInternal && <StyledLink to="/netapp-internal" variant="text" style={{ position: "absolute", right: 40, marginTop: 15, zIndex: 1 }} onClick={() => {
                        dispatch({
                            type: "MENU:CLOSE-WIDGET"
                        })
                    }}>{t("netApp_internal")}</StyledLink>}
                </StaticDetails>
                <DarkTheme/>
            </div>
    </SlidingWidget>
};
